import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { ConfigProvider, Drawer, Input, Pagination, Select, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCar,
  faChevronDown,
  faCircleExclamation,
  faDownload,
  faEnvelope,
  faFileArrowUp,
  faHotel,
  faMagnifyingGlass,
  faPlane,
  faPlus,
  faShip,
  faSuitcase,
  faSuitcaseRolling,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { axiosInstance, axiosInstanceB2B, FunctionalRootState, RootState, Toaster } from '@share/utils';
import { getBookingManual } from '../../../../../share/store/slices/booking-manual';
import { IBookingManualSort, IFiltersManualBooking, ManualBookingData } from '../../../dashboard/dashboard-manual-booking/component';
import ManualBookingForm from '../../../dashboard/dashboard-manual-booking/manual-booking-form/component';
import { getHubThemeStyle, parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';
// Same status / search / sort toolbar as My bookings.
import filterStyles from '../bookings/styles.module.scss';

import styles from './styles.module.scss';

const PAGE_SIZES = [10, 20, 40];
const SEARCH_DEBOUNCE_MS = 400;
const getDrawerPopupContainer = (trigger?: HTMLElement): HTMLElement => trigger?.parentElement || document.body;

// The list API answers an empty result with this error text; it means "nothing yet", not a failure.
const EMPTY_RESULT_ERROR = 'No data available';

// Every column the dashboard can export, so the emailed file has the full record.
const EXPORT_COLUMNS = [
  'manualBookingId', 'agentName', 'bookingId', 'numberOfTravelers', 'travelDateStart', 'travelDateEnd', 'isPaid',
  'refundAdjustment', 'contactInformation', 'bookingDate', 'bookingType', 'clientName', 'serviceProviderName',
  'serviceConfirmationNumber', 'detailsOfServices', 'totalBookingAmount', 'totalCommission', 'refundsOrAdjustments',
  'dateOfSubmission', 'created', 'lastUpdated', 'pathFile',
];

type PaidKey = 'all' | 'paid' | 'unpaid';
type SortKey = 'submitted' | 'travel' | 'trip';

const SORTS: Record<SortKey, IBookingManualSort> = {
  // The list API reads `sortOrder` as the column and `orderBy` as the direction.
  submitted: { sortOrder: 'dateOfSubmission', orderBy: 'desc' },
  travel: { sortOrder: 'travelDateStart', orderBy: 'desc' },
  trip: { sortOrder: 'bookingId', orderBy: 'asc' },
};

const TYPE_ICONS: Record<string, IconDefinition> = {
  hotel: faHotel,
  condo: faBuilding,
  car: faCar,
  flight: faPlane,
  cruise: faShip,
  'vacation package': faSuitcaseRolling,
};

const CHANGE_LABELS: Record<string, string> = {
  NewBooking: 'my.account.hub.manual.change.new',
  ChangeBooking: 'my.account.hub.manual.change.updated',
  CancellationBooking: 'my.account.hub.manual.change.cancellation',
};

// The list payload also carries these, which the dashboard's row type doesn't declare.
interface IManualBookingRow extends ManualBookingData {
  isPaid?: boolean;
  fileName?: string;
}

/**
 * Manual bookings in the hub: the same list API, filters, export and form as My Dashboard's
 * "Bookings - Manual Upload", shown as ledger rows with the form in a side drawer.
 */
export const AccountManualUpload = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const bookingManualStore = useSelector((state: FunctionalRootState) => state.bookingManualStore);
  const items = useSelector((state: FunctionalRootState) => state.navigationMenuStore.items);
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const user = useSelector((state: FunctionalRootState) => state.loginStore.user);

  const agentID = items?.rsiId ?? 0;
  const { bookingManual, loadingBookingManual, bookingManualError } = bookingManualStore;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [paid, setPaid] = useState<PaidKey>('all');
  const [sortKey, setSortKey] = useState<SortKey>('submitted');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Wait until typing pauses before searching, then start from the first page.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const pagination = useMemo(() => ({ current: page, pageSize }), [page, pageSize]);
  const sort = SORTS[sortKey];
  const filters = useMemo<IFiltersManualBooking>(() => ({
    agentName: '',
    bookingId: search,
    isPaid: paid === 'all' ? undefined : paid === 'paid',
  }), [search, paid]);

  useEffect(() => {
    if (agentID) {
      dispatch(getBookingManual(pagination, agentID, sort, filters));
    }
  }, [dispatch, agentID, pagination, sort, filters, reloadKey]);

  const rows: IManualBookingRow[] = (bookingManual?.items || []) as IManualBookingRow[];
  const total = bookingManual?.totalResults || 0;
  const isEmptyResult = bookingManualError === EMPTY_RESULT_ERROR;
  const failed = !!bookingManualError && !isEmptyResult;
  const isFiltered = !!search || paid !== 'all';

  const money = (value: string | number | null | undefined) => {
    const amount = Number(value);
    return Number.isFinite(amount) && value !== '' && value !== null && value !== undefined
      ? intl.formatNumber(amount, { style: 'currency', currency: 'USD' })
      : '—';
  };
  const shortDate = (value: string | null | undefined) => {
    const date = parseApiDate(value);
    return date ? intl.formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  };

  const handleDownload = async (row: IManualBookingRow) => {
    try {
      const response = await axiosInstance.get(
        `/manualbooking/${encodeURIComponent(String(row.manualBookingId))}/file`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', row.fileName || `manual-booking-${row.manualBookingId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Account hub: manual booking file download failed', error);
      Toaster.error(intl.formatMessage({ id: 'bookingmanual.pathFile.download.error' }));
    }
  };

  const handleExport = async () => {
    const memberID = user?.keyid;
    const email = user?.email;
    if (!memberID || !email) {
      Toaster.error(intl.formatMessage({ id: 'my.account.hub.manual.export_missing' }));
      return;
    }
    // Same request My Dashboard sends, with every value encoded.
    const params = new URLSearchParams({
      emailExport: email,
      agentID: String(memberID),
      exportDefault: 'false',
      nameFile: `bookings_manual_export_${new Date().toISOString().slice(0, 10)}`,
      orderBy: 'bookingId',
      sortOrder: 'desc',
      IsFromBff: 'true',
    });
    EXPORT_COLUMNS.forEach((column, index) => params.append(`columns[${index}]`, column));
    try {
      await axiosInstanceB2B.get(`/bookings/manualbooking/export?${params.toString()}`);
      Toaster.success(intl.formatMessage({ id: 'my.account.hub.manual.export_sent' }, { email }));
    } catch (error) {
      console.error('Account hub: manual booking export failed', error);
      Toaster.error(intl.formatMessage({ id: 'my.account.hub.manual.export_failed' }));
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setPaid('all');
    setPage(1);
  };

  const renderPaid = (row: IManualBookingRow) => {
    if (row.refundAdjustment === 'CancellationBooking') {
      return <span className={`${styles.badge} ${styles.badgeNeutral}`}>{intl.formatMessage({ id: 'my.account.hub.manual.paid.cancelled' })}</span>;
    }
    return row.isPaid
      ? <span className={`${styles.badge} ${styles.badgeOk}`}>{intl.formatMessage({ id: 'my.account.hub.manual.paid.paid' })}</span>
      : <span className={`${styles.badge} ${styles.badgeWarning}`}>{intl.formatMessage({ id: 'my.account.hub.manual.paid.unpaid' })}</span>;
  };

  const renderRow = (row: IManualBookingRow) => {
    const start = parseApiDate(row.travelDateStart);
    const end = parseApiDate(row.travelDateEnd);
    const isOpen = !!expanded[row.manualBookingId];
    const detailsId = `manual-booking-${row.manualBookingId}-details`;
    const typeKey = (row.bookingType || '').toLowerCase();
    const changeLabel = CHANGE_LABELS[row.refundAdjustment];
    const dates = start
      ? [intl.formatDate(start, { day: 'numeric', month: 'short' }), end ? intl.formatDate(end, { day: 'numeric', month: 'short', year: 'numeric' }) : '']
        .filter(Boolean).join(' – ')
      : '';

    return (
      <li key={row.manualBookingId} className={styles.manualRow}>
        <div className={styles.manualMain}>
          <div className={styles.manualDate} aria-hidden={!start}>
            {start ? (
              <>
                <span className={styles.manualMonth}>{intl.formatDate(start, { month: 'short' })}</span>
                <span className={styles.manualDay}>{intl.formatDate(start, { day: '2-digit' })}</span>
              </>
            ) : <span className={styles.manualDay}>—</span>}
          </div>

          <div className={styles.manualBody}>
            <div className={styles.manualTitleRow}>
              <h3 className={styles.manualTitle}>{row.bookingId || intl.formatMessage({ id: 'my.account.hub.manual.untitled' })}</h3>
              {renderPaid(row)}
              {changeLabel ? <span className={`${styles.badge} ${styles.badgeQuiet}`}>{intl.formatMessage({ id: changeLabel })}</span> : null}
            </div>
            <p className={styles.manualSubtitle}>
              <FontAwesomeIcon icon={TYPE_ICONS[typeKey] || faSuitcase} aria-hidden="true" />
              {[row.bookingType, row.clientName, row.serviceProviderName].filter(Boolean).join(' · ')}
            </p>
            <p className={styles.manualMeta}>
              {[
                dates,
                row.numberOfTravelers ? intl.formatMessage({ id: 'my.account.hub.manual.travelers' }, { count: row.numberOfTravelers }) : '',
                row.serviceConfirmationNumber ? intl.formatMessage({ id: 'my.account.hub.manual.confirmation' }, { code: row.serviceConfirmationNumber }) : '',
              ].filter(Boolean).join(' · ')}
            </p>
          </div>

          <div className={styles.manualAmounts}>
            <span className={styles.manualTotal}>{money(row.totalBookingAmount)}</span>
            <span className={styles.manualCommission}>
              {intl.formatMessage({ id: 'my.account.hub.manual.commission' }, { amount: money(row.totalCommission) })}
            </span>
            <button
              type="button"
              className={styles.manualToggle}
              aria-expanded={isOpen}
              aria-controls={detailsId}
              onClick={() => setExpanded(value => ({ ...value, [row.manualBookingId]: !isOpen }))}
            >
              {intl.formatMessage({ id: isOpen ? 'my.account.hub.manual.hide_details' : 'my.account.hub.manual.details' })}
              <FontAwesomeIcon icon={faChevronDown} className={isOpen ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
            </button>
          </div>
        </div>

        {isOpen ? (
          <div id={detailsId} className={styles.manualDetails}>
            <dl>
              {row.detailsOfServices ? (
                <div className={styles.manualDetailsWide}>
                  <dt>{intl.formatMessage({ id: 'bookingmanual.detailsOfServices' })}</dt>
                  <dd>{row.detailsOfServices}</dd>
                </div>
              ) : null}
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.manual.reference' })}</dt>
                <dd>#{row.manualBookingId}</dd>
              </div>
              <div>
                <dt>{intl.formatMessage({ id: 'bookingmanual.bookingDate' })}</dt>
                <dd>{shortDate(row.bookingDate) || '—'}</dd>
              </div>
              <div>
                <dt>{intl.formatMessage({ id: 'bookingmanual.dateOfSubmission' })}</dt>
                <dd>{shortDate(row.dateOfSubmission) || '—'}</dd>
              </div>
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.manual.last_updated' })}</dt>
                <dd>{shortDate(row.lastUpdated) || '—'}</dd>
              </div>
              <div>
                <dt>{intl.formatMessage({ id: 'my.account.hub.manual.contact' })}</dt>
                <dd>{row.contactInformation || '—'}</dd>
              </div>
            </dl>
            {row.pathFile ? (
              <button type="button" className={styles.secondaryButton} onClick={() => handleDownload(row)}>
                <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                {intl.formatMessage({ id: 'bookingmanual.pathFile.download' })}
              </button>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  };

  const renderList = () => {
    if (loadingBookingManual && !rows.length) {
      return (
        <ul className={styles.manualList} aria-busy="true">
          {[0, 1, 2].map(key => (
            <li key={key} className={styles.manualRow}><Skeleton active title={false} paragraph={{ rows: 2 }} /></li>
          ))}
        </ul>
      );
    }
    if (failed) {
      return (
        <AccountEmptyState
          variant="panel"
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.manual.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.manual.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: () => setReloadKey(key => key + 1) }}
        />
      );
    }
    if (!rows.length) {
      return isFiltered ? (
        <AccountEmptyState
          variant="panel"
          icon={faMagnifyingGlass}
          title={intl.formatMessage({ id: 'my.account.hub.manual.no_match' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.clear_filters' }), onClick: clearFilters }}
        />
      ) : (
        <AccountEmptyState
          variant="panel"
          icon={faFileArrowUp}
          title={intl.formatMessage({ id: 'my.account.hub.manual.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.manual.empty_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.manual.add' }), onClick: () => setFormOpen(true) }}
        />
      );
    }
    return (
      <ul className={loadingBookingManual ? `${styles.manualList} ${styles.refreshing}` : styles.manualList}>
        {rows.map(renderRow)}
      </ul>
    );
  };

  return (
    <div className={styles.agentTool}>
      <div className={styles.manualHeader}>
        <p className={styles.intro}>{intl.formatMessage({ id: 'my.account.hub.agent.manual_upload_intro' })}</p>
        <div className={styles.siteActions}>
          <button type="button" className={styles.primaryButton} onClick={() => setFormOpen(true)}>
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
            {intl.formatMessage({ id: 'my.account.hub.manual.add' })}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleExport} title={user?.email ? intl.formatMessage({ id: 'my.account.hub.manual.export_note' }, { email: user.email }) : undefined}>
            <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
            {intl.formatMessage({ id: 'my.account.hub.manual.export' })}
          </button>
        </div>
      </div>

      <div className={filterStyles.toolbar}>
        <div role="tablist" aria-label={intl.formatMessage({ id: 'my.account.hub.manual.paid_label' })} className={filterStyles.segmented}>
          {(['all', 'paid', 'unpaid'] as PaidKey[]).map(key => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={paid === key}
              className={paid === key ? `${filterStyles.segment} ${filterStyles.segmentActive}` : filterStyles.segment}
              onClick={() => {
                setPaid(key);
                setPage(1);
              }}
            >
              {intl.formatMessage({ id: `my.account.hub.manual.filter.${key}` })}
            </button>
          ))}
        </div>
        <div className={filterStyles.controls}>
          <div className={styles.manualSearch}>
            <Input
              allowClear
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />}
              placeholder={intl.formatMessage({ id: 'my.account.hub.manual.search' })}
              aria-label={intl.formatMessage({ id: 'my.account.hub.manual.search' })}
            />
          </div>
          <Select<SortKey>
            value={sortKey}
            onChange={value => {
              setSortKey(value);
              setPage(1);
            }}
            className={filterStyles.sort}
            dropdownMatchSelectWidth={false}
            aria-label={intl.formatMessage({ id: 'my.account.hub.manual.sort' })}
            options={[
              { value: 'submitted', label: intl.formatMessage({ id: 'my.account.hub.manual.sort.submitted' }) },
              { value: 'travel', label: intl.formatMessage({ id: 'my.account.hub.manual.sort.travel' }) },
              { value: 'trip', label: intl.formatMessage({ id: 'my.account.hub.manual.sort.trip' }) },
            ]}
          />
        </div>
      </div>

      {rows.length ? (
        <p className={filterStyles.count}>{intl.formatMessage({ id: 'my.account.hub.manual.count' }, { count: total })}</p>
      ) : null}

      {renderList()}

      {total > pageSize || pageSize !== PAGE_SIZES[0] ? (
        <div className={styles.manualPager}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={PAGE_SIZES.map(String)}
            onChange={(nextPage, nextSize) => {
              if (nextSize && nextSize !== pageSize) {
                setPageSize(nextSize);
                setPage(1);
              } else {
                setPage(nextPage);
              }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      ) : null}

      <Drawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        placement="right"
        // Full width on phones, 720px otherwise.
        width="min(720px, 100vw)"
        zIndex={4100}
        title={intl.formatMessage({ id: 'my.account.hub.manual.form_title' })}
        className={styles.manualDrawer}
        style={getHubThemeStyle(account)}
        destroyOnClose
      >
        {/*
          The drawer sits above the site header (z-index 4100), but antd opens Select / DatePicker /
          Tooltip popups on <body> at ~1050, i.e. behind it. Render them inside the drawer instead.
        */}
        <ConfigProvider getPopupContainer={getDrawerPopupContainer}>
          <ManualBookingForm
            embedded
            bookingManualStore={bookingManualStore}
            pagination={pagination}
            agentID={agentID}
            filters={filters}
            sort={sort}
            onSubmitted={() => setFormOpen(false)}
          />
        </ConfigProvider>
      </Drawer>
    </div>
  );
};
